<template>
  <div>
    <div>
      <input
        :name="name"
        v-model="countryCode"
        class="search-input"
        :class="[focusClassHandler, heightClass, widthClass]"
        :disabled="disabled"
        autocomplete="off"
        :placeholder="placeholder"
        @focus="showOptions()"
        @blur="exit()"
      />
      <div v-if="isFocused" class="flex justify-between">
        <search-icon class="input-img"
        :class="height < 38 ? 'top-0' : 'top-10'" />
        <clear-icon @click="exit()" class="absolute right-2 top-10 cursor-pointer" />
      </div>
      <img :src="image" v-else class="w-20 h-20"
       :class="height <= 24 ? 'absolute top-1 left-3' : 'input-img top-10'" />
      <arrow-down-icon v-if="!isFocused" class="input-icon"
      :class="height < 38 ? 'top-0' : 'top-10'"
       @click="closeInput" />
    </div>
    <transition name="transform">
      <div class="countries-wrapper" v-show="isOptionsShown" :class="widthClass">
        <div
          v-for="(option, index) in filteredOptions"
          :key="index"
          class="country-item"
          @mousedown="selectedOption(options[index])"
        >
          <img :src="option.image" @error="OnImageError" class="w-20 h-20" />
          <p>{{ option.name }}</p>
          <p dir="ltr">({{ option.code.replace('00', '+') }})</p>
        </div>
        <p v-if="isResultEmpty">
          {{ $t('no_item_yet') }}
        </p>
      </div>
    </transition>
  </div>
</template>

<script>
import SearchIcon from '@/assets/icons/vue/search_normal.vue';
import ArrowDownIcon from '@/assets/icons/vue/arrow_down.vue';
import clearIcon from '@/assets/icons/vue/close_normal.vue';
import irFlag from '@/assets/icons/svg/irFlag.svg';
import { OnImageError } from '@/utils/on-image-error';

export default {
  name: 'BaseCombobox',
  components: {
    ArrowDownIcon,
    SearchIcon,
    clearIcon,
  },
  props: {
    name: {
      type: String,
      required: false,
      default: 'dropdown',
    },
    options: {
      type: Array,
      required: true,
      default: null,
    },
    placeholder: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    userCountryCode: {
      type: String,
    },
    height: {
      type: String,
      default: '38',
    },
    width: {
      type: String,
      default: '318',
    },
  },
  data() {
    return {
      OnImageError,
      searchFilter: '',
      image: null,
      icon: null,
      isOptionsShown: false,
      isFocused: false,
      isResultEmpty: false,
      isClearable: false,
      selected: {},
    };
  },
  mounted() {
    this.selected = {
      image: irFlag,
      name: this.$i18n.t('iran'),
      code: '0098',
      pattern: '*** *** ** **',
    };
    this.searchFilter = this.selected.code;
    this.image = this.selected.image;
    this.$emit('selected', this.selected);
  },
  computed: {
    countryCode() {
      return this.searchFilter.replace('00', '+');
    },
    heightClass() {
      return `h-${this.height}`;
    },
    widthClass() {
      return !this.isFocused ? 'w-88' : `w-${this.width}`;
    },
    filteredOptions() {
      const filtered = [];
      const searchText = this.searchFilter;
      this.options.forEach((option) => {
        const optionName = option.name.toLowerCase();
        const optionCode = option.code;
        if (
          this.searchFilter === ''
          || optionName.includes(searchText)
          || optionCode.includes(searchText)
        ) {
          this.isResultEmpty = false;
          filtered.push(option);
        }
      });
      return filtered;
    },
    focusClassHandler() {
      return !this.isFocused ? 'bg-tertiary' : 'rounded-b-none bg-tertiary';
    },
  },
  methods: {
    selectedOption(option) {
      this.selected = option;
      this.isOptionsShown = false;
      this.searchFilter = this.selected.code;
      this.image = this.selected.image;
      this.$emit('selected', this.selected);
      this.$emit('changedCode', option.code);
    },
    closeInput() {
      this.isFocused = false;
    },
    showOptions() {
      if (!this.disabled) {
        this.isFocused = true;
        this.isOptionsShown = true;
        this.searchFilter = '';
        this.icon = clearIcon;
      }
    },
    exit() {
      this.searchFilter = this.selected.code;
      this.image = this.selected.image;
      this.isOptionsShown = false;
      this.isFocused = false;
    },
  },
  watch: {
    searchFilter() {
      if (this.filteredOptions.length === 0) {
        this.isResultEmpty = true;
      } else {
        this.isResultEmpty = false;
      }
    },
    options() {
      if (this.userCountryCode) {
        this.selected = this.options.filter(
          (option) => option.code === this.userCountryCode,
        )[0];
        this.$emit('selected', this.selected);
        this.image = this.selected.image;
        this.searchFilter = this.selected.code;
      }
    },
  },
};
</script>
<style scoped>
/* START TRANSITION */
.ease-custom {
  transition-timing-function: cubic-bezier(0.61, -0.53, 0.43, 1.43);
}
.transform-enter-active,
.transform-leave-active {
  @apply transform transition duration-500 ease-custom;
}
.transform-enter,
.transform-leave-to {
  @apply -translate-y-1/2 scale-0 -translate-x-1/2 opacity-0;
}
.transform-enter-to,
.transform-leave {
  @apply translate-y-0 scale-100 translate-x-0 opacity-100;
}
/* END TRANSITION */

.search-input {
  @apply outline-none rounded-sm px-30 transition-all
        duration-500 ease-custom font-bold text-12 flex items-center
        hover:cursor-pointer focus:rounded-b-none;
}
.search-input::placeholder {
  @apply font-medium not-italic text-sm text-subtitle leading-4;
}
.search-input:is(:lang(fa), :lang(ar)) {
  direction: ltr;
}
.input-img {
  @apply absolute left-2;
}
.input-icon {
  @apply absolute right-5 hover:cursor-pointer;
}
.countries-wrapper {
  @apply bg-tertiary border-t border-default border-opacity-20 rounded-b p-2 z-10;
}
.countries-wrapper:is(:lang(fa), :lang(ar)) {
  direction: rtl;
}
.country-item {
  @apply grid grid-flow-col auto-cols-max gap-4 place-items-center p-2 w-full
rounded cursor-pointer transition duration-300 hover:bg-secondary;
}
</style>
